/**
 * ⚡ SPEED DATING TAB - Mode Mozart/Jobs/Tesla
 * 
 * Gestion des inscriptions au Speed Dating LPE
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gift, Clock, Shield } from 'lucide-react';
import { Button } from '../../ui/button';
import { api } from '../../../services/api';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

function SpeedDatingTab({ customer, isDarkMode }) {
  const [slots, setSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [slotsData, statusData] = await Promise.all([
          api.getSpeedDateSlots(),
          api.getSpeedDateStatus(customer?.id)
        ]);
        setSlots(slotsData.slots || []);
        setStatus(statusData);
      } catch (error) {
        console.error('Erreur chargement speed dating:', error);
      } finally {
        setLoading(false);
      }
    };
    if (customer?.id) loadData();
  }, [customer]);

  const toggleSlot = (slotId) => {
    setSelectedSlots(prev => 
      prev.includes(slotId) 
        ? prev.filter(s => s !== slotId)
        : [...prev, slotId]
    );
  };

  const handleRegister = async () => {
    if (selectedSlots.length === 0) {
      toast.error("Sélectionne au moins un créneau !");
      return;
    }
    setRegistering(true);
    try {
      const result = await api.registerSpeedDate(customer.id, selectedSlots);
      setStatus(result);
      if (result.status === 'matched') {
        toast.success("🎉 On t'a trouvé quelqu'un !");
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#8B5CF6', '#EC4899', '#F59E0B']
        });
      } else {
        toast.success("✅ Tu es inscrit(e) !");
      }
    } catch (error) {
      toast.error("Erreur lors de l'inscription");
    } finally {
      setRegistering(false);
    }
  };

  const handleCancel = async () => {
    try {
      await api.cancelSpeedDate(customer.id);
      setStatus({ status: 'not_registered' });
      setSelectedSlots([]);
      toast.success("Inscription annulée");
    } catch (error) {
      toast.error("Erreur lors de l'annulation");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Gift className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  // Si déjà matché
  if (status?.status === 'matched') {
    const slotLabel = slots.find(s => s.id === status.scheduledSlot)?.label || status.scheduledSlot;
    return (
      <div className="text-center py-8">
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          className="mb-6"
        >
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-5xl mb-4">
            {status.matchPreview?.avatar || '🎁'}
          </div>
          <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            RDV confirmé ! 🎉
          </h3>
          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
            Tu as un Speed Date de prévu
          </p>
        </motion.div>

        <div className={`p-6 rounded-2xl mb-6 ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
          <div className="flex items-center justify-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-purple-500" />
            <span className={`font-bold ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
              {slotLabel}
            </span>
          </div>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            📍 RDV à La Petite Épicerie
          </p>
          {status.matchPreview?.commonInterests?.length > 0 && (
            <div className="mt-4">
              <p className={`text-xs uppercase tracking-wide mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Vous avez en commun
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {status.matchPreview.commonInterests.map((interest, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 rounded-full text-sm bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                  >
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <Button
          onClick={handleCancel}
          variant="outline"
          className={`${isDarkMode ? 'border-gray-600 text-gray-300' : ''}`}
        >
          Annuler mon inscription
        </Button>
      </div>
    );
  }

  // Si inscrit mais pas encore matché
  if (status?.status === 'registered') {
    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-4xl mb-4 animate-pulse">
          ⏳
        </div>
        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Tu es inscrit(e) !
        </h3>
        <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          On te préviendra dès qu'on trouve quelqu'un de compatible 💕
        </p>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Créneaux sélectionnés : {status.availableSlots?.length || 0}
        </p>
        <Button
          onClick={handleCancel}
          variant="outline"
          className={`${isDarkMode ? 'border-gray-600 text-gray-300' : ''}`}
        >
          Annuler mon inscription
        </Button>
      </div>
    );
  }

  // 🕐 Compte à rebours jusqu'au prochain Speed Dating
  const getNextSpeedDate = () => {
    const now = new Date();
    const nextSaturday = new Date(now);
    nextSaturday.setDate(now.getDate() + (6 - now.getDay() + 7) % 7 || 7);
    nextSaturday.setHours(15, 0, 0, 0);
    if (nextSaturday <= now) {
      nextSaturday.setDate(nextSaturday.getDate() + 7);
    }
    return nextSaturday;
  };

  const CountdownTimer = () => {
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    
    useEffect(() => {
      const target = getNextSpeedDate();
      const interval = setInterval(() => {
        const now = new Date();
        const diff = target - now;
        
        if (diff <= 0) {
          clearInterval(interval);
          return;
        }
        
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
      }, 1000);
      
      return () => clearInterval(interval);
    }, []);
    
    return (
      <div className={`p-4 rounded-2xl mb-6 text-center ${isDarkMode ? 'bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/30' : 'bg-gradient-to-r from-purple-100 to-pink-100'}`}>
        <p className={`text-xs uppercase tracking-wide mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
          ⏰ Prochain Speed Dating dans
        </p>
        <div className="flex justify-center gap-3">
          {[
            { value: timeLeft.days, label: 'J' },
            { value: timeLeft.hours, label: 'H' },
            { value: timeLeft.minutes, label: 'M' },
            { value: timeLeft.seconds, label: 'S' }
          ].map((item, idx) => (
            <div key={idx} className="flex flex-col items-center">
              <motion.div
                key={item.value}
                initial={{ scale: 1.2, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl font-black ${
                  isDarkMode 
                    ? 'bg-purple-500/30 text-white' 
                    : 'bg-white text-purple-700 shadow-md'
                }`}
              >
                {String(item.value).padStart(2, '0')}
              </motion.div>
              <span className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
        <p className={`text-xs mt-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          📍 Samedi 15h à La Petite Épicerie
        </p>
      </div>
    );
  };

  // Formulaire d'inscription
  return (
    <div className={isDarkMode ? 'bg-gray-900' : 'bg-white'}>
      <CountdownTimer />
      
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-3xl mb-3">
          🎁
        </div>
        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Speed Dating LPE
        </h3>
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
          On t'organise un RDV surprise avec quelqu'un de compatible !
        </p>
      </div>

      <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <h4 className={`font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          📅 Quand es-tu disponible ?
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {slots.map((slot) => (
            <button
              key={slot.id}
              onClick={() => toggleSlot(slot.id)}
              className={`p-3 rounded-xl text-left transition-all ${
                selectedSlots.includes(slot.id)
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                  : isDarkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              <span className="text-lg mr-2">{slot.icon}</span>
              <span className="text-sm">{slot.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
        <h4 className={`font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
          <Shield className="w-4 h-4" />
          Comment ça marche ?
        </h4>
        <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          <li>✓ On te trouve quelqu'un avec des intérêts communs</li>
          <li>✓ Vous ne savez pas à l'avance qui c'est (surprise !)</li>
          <li>✓ RDV à l'épicerie pour un moment convivial</li>
          <li>✓ Si ça matche, on vous met en contact après</li>
        </ul>
      </div>

      <Button
        onClick={handleRegister}
        disabled={selectedSlots.length === 0 || registering}
        className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold"
      >
        {registering ? (
          <span className="flex items-center gap-2">
            <Gift className="w-5 h-5 animate-spin" />
            Inscription...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Gift className="w-5 h-5" />
            Je m'inscris ({selectedSlots.length} créneau{selectedSlots.length > 1 ? 'x' : ''})
          </span>
        )}
      </Button>
    </div>
  );
}

export default SpeedDatingTab;
